import { zodResolver } from "@hookform/resolvers/zod";
import { Stack, FormHelperText, Typography, Box } from "@mui/joy";
import { useMemo, useEffect } from "react";
import { FormProvider, useForm, useFormContext } from "react-hook-form";
import { useWatch } from "react-hook-form";

import { useLeagueQuery, useUpdateLeagueMutation } from "@/hooks/competitions";
import { useCurrentVenueUuid } from "@/hooks/venues";
import { handleFormError } from "@/utils/handleFormError";

import FormErrorAlert from "../FormErrorAlert";
import CheckboxInput from "../forms/CheckboxInput";
import { NumberInput } from "../forms/TextInput";
import { ToggleButtonSelectInput } from "../forms/ToggleButtonSelect";
import BottomButtons from "./BottomButtons";
import { leagueToScoringFormValues, ScoringFormSchema, ScoringFormValues } from "./forms";

type Props = {
  leagueUuid: string;
};

export default function LeagueScoringForm({ leagueUuid }: Props) {
  const { data: league } = useLeagueQuery(leagueUuid);
  const venueUuid = useCurrentVenueUuid();
  // The API types scoringSystem as any string. The zod resolver checks it
  // against the form's choices on submit.
  const defaultValues = useMemo(
    () => leagueToScoringFormValues(league) as ScoringFormValues,
    [league],
  );
  const methods = useForm({
    mode: "onBlur",
    resolver: zodResolver(ScoringFormSchema),
    defaultValues,
  });

  const updateMutation = useUpdateLeagueMutation(leagueUuid, venueUuid);

  useEffect(() => {
    methods.reset(defaultValues);
  }, [defaultValues, methods]);

  useEffect(() => {
    localStorage.setItem(`leagues:${leagueUuid}:hideCategoriesWarning`, JSON.stringify(false));
  }, [leagueUuid]);

  const onSubmit = async (values: ScoringFormValues) => {
    const payload = {
      scoringSystem: values.scoringSystem,
      topRoundsCount: values.topRoundsCount,
    };

    try {
      await updateMutation.mutateAsync(payload);
    } catch (error) {
      handleFormError(error, methods);
    }
  };

  return (
    <Stack
      sx={{ padding: 1.5, gap: 1.5, flex: 1, overflowY: "auto" }}
      component="form"
      autoComplete="off"
      onSubmit={methods.handleSubmit(onSubmit)}
    >
      <FormProvider {...methods}>
        <Stack flex={1} spacing={1}>
          <FormErrorAlert />
          <FormFields />
        </Stack>
        <BottomButtons
          formState={methods.formState}
          isFinalStep
          prevLink={{
            to: "/gym/$venueUuid/events/series/$leagueUuid/profile/categories",
            replace: true,
          }}
          league={league}
        />
      </FormProvider>
    </Stack>
  );
}

const FormFields = () => {
  const scoringSystemOptions = [
    {
      value: "SIMPLE",
      name: "Addition",
    },
    {
      value: "OLYMPICS",
      name: "Multiplication",
    },
  ];
  const helpTextBySystem = {
    SIMPLE: "Scores from contributing rounds are added together.",
    OLYMPICS:
      "Positions from contributing rounds are multiplied together - lower is better. E.g. 10th × 16th × 2nd = 320 points",
  };
  const scoringSystem: "SIMPLE" | "OLYMPICS" = useWatch({
    name: "scoringSystem",
  });
  const allRoundsContribute = useWatch({
    name: "allRoundsContribute",
  });
  const topRoundsCount = useWatch({
    name: "topRoundsCount",
  });

  const { setValue } = useFormContext();

  useEffect(() => {
    if (allRoundsContribute) {
      setValue("topRoundsCount", null);
    }
  }, [allRoundsContribute, setValue]);

  return (
    <>
      <ToggleButtonSelectInput
        name="scoringSystem"
        label="Scoring method"
        options={scoringSystemOptions}
        sx={{ marginBottom: -1 }}
      />
      <FormHelperText>{helpTextBySystem[scoringSystem]}</FormHelperText>
      <Typography fontWeight="bold" fontSize={15}>
        Round contribution
      </Typography>
      <CheckboxInput label="All rounds contribute to the result" name="allRoundsContribute" />
      {!allRoundsContribute && (
        <>
          <Box sx={{ maxWidth: 300 }}>
            <NumberInput label="Top rounds" name="topRoundsCount" />
          </Box>
          {topRoundsCount ? (
            <FormHelperText>
              {scoringSystem === "SIMPLE"
                ? `The final score is the sum of each competitor's best ${topRoundsCount} round score${
                    topRoundsCount > 1 ? "s" : ""
                  }.`
                : `The final score is the product of the competitor's best ${topRoundsCount} round position${
                    topRoundsCount > 1 ? "s" : ""
                  }.`}
            </FormHelperText>
          ) : null}
        </>
      )}
      <BottomButtons></BottomButtons>
    </>
  );
};
